import { Dropdown } from '@praetorian-chariot/ui';
import React, { useEffect, useMemo, useState } from 'react';

import { Container } from '@/components/ui';
import { EvidenceFile } from '@/hooks/useEvidenceFiles';
import { AssetStatusComponent } from '@/sections/asset/components/badges';
import { POE, POEProps } from '@/sections/POE';
import { Webpage } from '@/types';
import type { DropdownOption } from '@/types/dropdown.types';
import { cn } from '@/utils/classname';
import { formatDate } from '@/utils/date.util';

const Item = ({ selectedFile }: { selectedFile: EvidenceFile | undefined }) => {
  return (
    <div className="bg-chariot-background-secondary flex w-full items-center gap-3 p-4">
      <div className="flex flex-col text-left">
        <span className="text-chariot-text-primary text-sm font-semibold">
          {selectedFile?.webpage?.url}
        </span>
        <span className="text-chariot-text-secondary text-xs">
          Content Length: {selectedFile?.webpage?.metadata.content_length}
        </span>
        <span className="text-chariot-text-secondary text-xs">
          {formatDate(selectedFile?.webpage?.created || '')}
        </span>
      </div>
      <AssetStatusComponent
        className="ml-auto"
        asset={selectedFile?.webpage as Webpage}
      />
    </div>
  );
};

/**
 * web-secrets risks can have multiple evidence files.
 * This component allows the user to select which evidence file to use.
 */
export const POESelector: React.FC<
  POEProps & { evidenceFiles: EvidenceFile[] }
> = props => {
  const { evidenceFiles } = props;
  const evidenceFilesOptions = useMemo(
    () =>
      (evidenceFiles || []).map(file => ({
        label: file?.webpage?.url || file.attachmentPath,
        value: file.attachmentPath,
      })),
    [evidenceFiles]
  );

  const [selectedFileOption, setSelectedFileOption] = useState<
    DropdownOption | undefined
  >(evidenceFilesOptions[0]);

  useEffect(() => {
    if (evidenceFilesOptions.length > 0) {
      setSelectedFileOption(evidenceFilesOptions[0]);
    }
  }, [evidenceFilesOptions]);

  const selectedFile = useMemo(() => {
    return evidenceFiles?.find(
      file => file.attachmentPath === selectedFileOption?.value
    );
  }, [evidenceFiles, selectedFileOption]);

  const poeLocation =
    selectedFile?.attachmentPath.split('proofs/')[1] || props.poeLocation;

  const multipleFiles = evidenceFiles && evidenceFiles.length > 1;

  return (
    <div className="mb-6">
      {multipleFiles && (
        <div className="mb-4 flex items-center gap-2">
          <h2 className="text-sm font-semibold">Evidence files:</h2>
          <Dropdown
            size="xs"
            options={evidenceFiles?.map(file => ({
              label: file?.webpage?.url || file.attachmentPath,
              value: file.attachmentPath,
            }))}
            selected={selectedFileOption ? [selectedFileOption] : undefined}
            onSelect={option => {
              if (option) {
                setSelectedFileOption(option);
              }
            }}
          />

          <p className="text-chariot-text-secondary ml-auto text-sm">
            {evidenceFiles.length} Evidence file
            {evidenceFiles.length !== 1 ? 's' : ''} available
          </p>
        </div>
      )}
      <Container
        className={cn('size-full overflow-auto p-0')}
        tabIndex={-1}
        id={poeLocation}
        data-testid="evidence-content"
      >
        {multipleFiles && selectedFile && selectedFile?.webpage && (
          <Item selectedFile={selectedFile} />
        )}
        <POE
          {...props}
          key={poeLocation}
          poeLocation={poeLocation}
          webpage={evidenceFiles?.[0]?.webpage || selectedFile?.webpage}
        />
      </Container>
    </div>
  );
};
